import axiosInstance from "@/api/axios";
import { configToUse } from "@/Keycloak";
import {
  ITeamMembersResponse,
  IInviteUserRequest,
  IInviteUserResponse,
  IBulkInviteRequest,
  IBulkInviteResponse,
  IAcceptInviteResponse,
  IUpdateTeamMemberRequest,
  IDeleteTeamMemberRequest,
  IUpdateInviteResponse,
  IDeleteInviteResponse,
  IRetryInvitesResponse,
  IOverviewPromptsResponse,
  ICreateOverviewPromptRequest,
  ICreateOverviewPromptResponse,
  IUpdateOverviewPromptRequest,
  IUpdateOverviewPromptResponse,
  IDeleteOverviewPromptResponse,
  UserRole,
} from "./types";

import { DataConfigurations, Metric } from "@/types";
import { SectionFilter } from "@/types";
import type { AccountDefaultDateKey } from "@/types";
import { WinningCriteriaPayload } from "./components/WinningCriteria/types";
import { CancelToken } from "axios";

export const fetchUserAccounts = async () => {
  const res = await axiosInstance.get(`/accounts/fetch-user-accounts/`, {
    baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
  });
  return res.data;
};

export const updateAccountStatus = async (accountId: string, active: boolean) => {
  const res = await axiosInstance.put(
    `/accounts/update-user-account-status/`,
    {
      account_id: accountId,
      active: active,
    },
    {
      baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
    },
  );
  return res.data;
};

export const deleteAccount = async (accountId: string) => {
  const res = await axiosInstance.delete(`/accounts/delete-user-account/`, {
    baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
    data: {
      account_id: accountId,
    },
  });
  return res.data;
};

export const updateUserProfilePicture = async (
  content_name: string,
  content_type: string,
) => {
  const res = await axiosInstance.put(
    `/accounts/update-user-profile-picture/`,
    {
      content_name: content_name,
      content_type: content_type,
    },
    {
      baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
    },
  );
  return res.data;
};

export const updateUserInfo = async (data: any) => {
  const res = await axiosInstance.put(`/accounts/update-user-info/`, data, {
    baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
  });
  return res.data;
};

// Team Management APIs
export const fetchTeamMembers = async (
  accountId: string,
  clientUrl: string,
): Promise<ITeamMembersResponse> => {
  const res = await axiosInstance.get<ITeamMembersResponse>(
    `/accounts/${accountId}/account-users/`,
    {
      baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
      params: {
        client_url: clientUrl,
      },
    },
  );
  return res.data;
};

export const inviteTeamMember = async (
  accountId: string,
  email: string,
  role: UserRole,
  clientUrl: string,
): Promise<IInviteUserResponse> => {
  const payload: IInviteUserRequest = {
    email,
    role,
    client_url: clientUrl,
  };

  const res = await axiosInstance.post<IInviteUserResponse>(
    `/accounts/${accountId}/invite-user/`,
    payload,
    {
      baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
    },
  );
  return res.data;
};

export const bulkInviteTeamMembers = async (
  emails: string[],
  accountIds: string[],
  role: UserRole,
  clientUrl: string,
): Promise<IBulkInviteResponse> => {
  const payload: IBulkInviteRequest = {
    emails,
    account_ids: accountIds,
    role,
    client_url: clientUrl,
  };

  const res = await axiosInstance.post<IBulkInviteResponse>(
    `/accounts/bulk-invite/`,
    payload,
    {
      baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
    },
  );
  return res.data;
};

export const acceptInvite = async (token: string): Promise<IAcceptInviteResponse> => {
  const res = await axiosInstance.post<IAcceptInviteResponse>(
    `/accounts/accept-invite/${token}/`,
    {},
    {
      baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
    },
  );
  return res.data;
};

export const updateTeamMember = async (
  accountId: string,
  userId: string,
  role: UserRole,
  clientUrl?: string,
): Promise<ITeamMembersResponse> => {
  const payload: IUpdateTeamMemberRequest = {
    user_id: userId,
    role,
    ...(clientUrl && { client_url: clientUrl }),
  };

  const res = await axiosInstance.put<ITeamMembersResponse>(
    `/accounts/${accountId}/account-users/`,
    payload,
    {
      baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
    },
  );
  return res.data;
};

export const deleteTeamMember = async (
  accountId: string,
  userId: string,
  accountIds?: string[],
  clientUrl?: string,
): Promise<ITeamMembersResponse> => {
  const payload: IDeleteTeamMemberRequest = {
    user_id: userId,
    ...(accountIds && accountIds.length > 0 && { account_ids: accountIds }),
    ...(clientUrl && { client_url: clientUrl }),
  };

  const res = await axiosInstance.delete<ITeamMembersResponse>(
    `/accounts/${accountId}/account-users/`,
    {
      data: payload,
      baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
    },
  );
  return res.data;
};

export const updateInvite = async (
  accountId: string,
  inviteId: string,
  role: UserRole,
): Promise<IUpdateInviteResponse> => {
  const payload = {
    invite_id: inviteId,
    role,
  };

  const res = await axiosInstance.put<IUpdateInviteResponse>(
    `/accounts/${accountId}/${inviteId}/`,
    payload,
    {
      baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
    },
  );
  return res.data;
};

export const deleteInvite = async (
  accountId: string,
  tokenId: string,
): Promise<IDeleteInviteResponse> => {
  const res = await axiosInstance.delete<IDeleteInviteResponse>(
    `/accounts/${accountId}/${tokenId}/`,
    {
      baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
    },
  );
  return res.data;
};

export const retryInvites = async (
  accountId: string,
  inviteTokenIds: string[],
  clientUrl: string,
): Promise<IRetryInvitesResponse> => {
  const payload = {
    invite_token_ids: inviteTokenIds,
    client_url: clientUrl,
  };

  const res = await axiosInstance.post<IRetryInvitesResponse>(
    `/accounts/${accountId}/token-retry/`,
    payload,
    {
      baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
    },
  );
  return res.data;
};

// Overview Prompts API
export const fetchOverviewPrompts = async (
  accountId: string,
): Promise<IOverviewPromptsResponse> => {
  const res = await axiosInstance.get<IOverviewPromptsResponse>(
    `/creative/${accountId}/overview-prompts/`,
    {
      baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
    },
  );
  return res.data;
};

export const createOverviewPrompt = async (
  accountId: string,
  payload: ICreateOverviewPromptRequest,
): Promise<ICreateOverviewPromptResponse> => {
  const res = await axiosInstance.post<ICreateOverviewPromptResponse>(
    `/creative/${accountId}/overview-prompts/`,
    payload,
    {
      baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
    },
  );
  return res.data;
};

export const updateOverviewPrompt = async (
  accountId: string,
  overviewId: string,
  payload: IUpdateOverviewPromptRequest,
): Promise<IUpdateOverviewPromptResponse> => {
  const res = await axiosInstance.put<IUpdateOverviewPromptResponse>(
    `/creative/${accountId}/overview-prompts/${overviewId}/`,
    payload,
    {
      baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
    },
  );
  return res.data;
};

export const deleteOverviewPrompt = async (
  accountId: string,
  overviewId: string,
): Promise<IDeleteOverviewPromptResponse> => {
  const res = await axiosInstance.delete<IDeleteOverviewPromptResponse>(
    `/creative/${accountId}/overview-prompts/${overviewId}/`,
    {
      baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
    },
  );
  return res.data;
};

export const saveWinningConfig = async (
  accountId: string,
  data: WinningCriteriaPayload,
) => {
  const res = await axiosInstance.post<{ success: string }>(
    `/creative/${accountId}/winning-config/`,
    data,
    { baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL },
  );
  return res.data;
};

export const saveDefaultMetrics = async (
  accountId: string,
  metric_ids: string[],
  confirm_update_sections: boolean,
) => {
  const res = await axiosInstance.put(
    `/accounts/${accountId}/update-account-default-metrics/`,
    { metric_ids: metric_ids, confirm_update_sections: confirm_update_sections },
    { baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL },
  );
  return res.data;
};

export const saveDefaultMetricsAndFilters = async (
  accountId: string,
  payload: {
    metric_ids?: string[];
    filters?: SectionFilter[];
    default_date?: AccountDefaultDateKey;
    confirm_update_sections: boolean;
  },
) => {
  const res = await axiosInstance.put<{ message?: string }>(
    `/creative/${accountId}/update-account-default-metrics-and-filters/`,
    payload,
    { baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL },
  );
  return res.data as { message?: string };
};

export const getDataConfigurations = async (
  accountId: string,
  cancelToken?: CancelToken,
) => {
  const res = await axiosInstance.get<{
    status: string;
    configuration: DataConfigurations;
  }>(`/creative/${accountId}/data-configurations/`, {
    baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
    cancelToken,
  });
  return res.data;
};

export const saveDefaultFilters = async (
  accountId: string,
  filters: SectionFilter[],
  confirm_update_sections: boolean,
) => {
  const res = await axiosInstance.put<{ message?: string }>(
    `/creative/${accountId}/update-account-default-filters/`,
    { filters: filters, confirm_update_sections: confirm_update_sections },
    { baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL },
  );
  return res.data as { message?: string };
};

export const updateAttributionWindows = async (
  accountId: string,
  attributionWindows: string[],
) => {
  const res = await axiosInstance.put<{ message: string }>(
    `/accounts/${accountId}/update-account-attribution-windows/`,
    {
      attribution_windows: attributionWindows,
    },
    {
      baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
    },
  );
  return res.data;
};

export const fetchCustomMetrics = async (accountId: string) => {
  const res = await axiosInstance.get<Metric[]>(
    `/insights-v2/${accountId}/custom-metric/`,
    {
      baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
    },
  );
  return res.data;
};

export const fetchFormulaHelperMetrics = async (accountId: string) => {
  const res = await axiosInstance.get<Metric[]>(
    `/insights-v2/${accountId}/custom-helper-metrics/`,
    {
      baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
    },
  );
  return res.data;
};

export const deleteCustomMetric = async (accountId: string, metricId: string) => {
  const res = await axiosInstance.delete<{ status: string }>(
    `/insights-v2/${accountId}/custom-metric/`,
    {
      baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
      data: {
        metric_id: metricId,
      },
    },
  );
  return res.data;
};

export const createCustomMetric = async (
  accountId: string,
  data: {
    name: string;
    formula: string;
    primary_metric_ids: string[];
    prefix?: string;
    suffix?: string;
  },
) => {
  const res = await axiosInstance.post<Metric>(
    `/insights-v2/${accountId}/custom-metric/`,
    data,
    { baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL },
  );
  return res.data;
};

export const updateCustomMetric = async (
  accountId: string,
  data: {
    name: string;
    metric_id: string;
    formula: string;
    primary_metric_ids: string[];
    prefix?: string;
    suffix?: string;
  },
) => {
  const res = await axiosInstance.put<Metric>(
    `/insights-v2/${accountId}/custom-metric/`,
    data,
    { baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL },
  );
  return res.data;
};

interface InstructionsData {
  account_id: string;
  additional_info: {
    description: string;
    instructions: string;
  };
}

export const updateInstructionsAccount = async (data: InstructionsData) => {
  const res = await axiosInstance.put<{ message: string }>(
    `/accounts/update-account-instructions/`,
    data,
    {
      baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
    },
  );
  return res.data;
};

export const updateAccountDefaultDate = async (
  accountId: string,
  defaultDate: AccountDefaultDateKey,
  confirm_update_sections: boolean = false,
) => {
  const res = await axiosInstance.put<{ message: string }>(
    `/creative/${accountId}/update-account-default-date/`,
    {
      default_date: defaultDate,
      confirm_update_sections,
    },
    {
      baseURL: configToUse.VITE_APP_FERROUS_API_BASE_URL,
    },
  );
  return res.data;
};
